import type { Reference } from '@medplum/fhirtypes'
import { referenceDisplay } from '@seen/core/display'
import { ExtendedClientApplication } from '@seen/core/models/clientApplication'
import { isOfType } from '@seen/core/utils'
import type { JSX, ReactNode } from 'react'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { HoverPractitioner } from '#src/components/core/hover-card/HoverPractitioner.tsx'
import { Logo } from '#src/components/core/Logo.tsx'
import { HoverTask } from '#src/components/HoverTask.tsx'
import { IconRelatedArrow } from '#src/components/icons/IconRelatedArrow.tsx'

interface MedicationHoverCardHistoryEntryProps {
  /* Line above the entry, aligned with the action text. */
  label?: ReactNode
  author?: Reference
  time?: Date | string
  timeFormat?: 'date' | 'datetime'
  timeTooltipDetail?: string
  action?: ReactNode
  originTask?: Reference
  nestedEvents?: {
    author?: Reference
    time?: Date | string
    timeFormat?: 'date' | 'datetime'
    action: ReactNode
    children?: ReactNode
  }[]
  children?: ReactNode
}

export function MedicationHoverCardHistoryEntry({
  label,
  author,
  time,
  timeFormat = 'datetime',
  timeTooltipDetail,
  action,
  originTask,
  nestedEvents,
  children,
}: MedicationHoverCardHistoryEntryProps): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      {label && <div className="ml-5">{label}</div>}
      <div className="flex items-start gap-4">
        <div className="flex min-w-0 flex-1 items-start gap-1">
          <HistoryAvatar author={author} />
          <div className="min-w-0 flex-1 text-xs leading-4">
            <HistoryAuthor author={author} />
            {action && <span className="text-muted-foreground"> {action}</span>}
            {originTask && (
              <span className="ml-1 inline-flex items-center gap-1 align-middle text-muted-foreground">
                <span>as part of</span>
                {isOfType(originTask, 'Task') ? (
                  <HoverTask task={originTask} />
                ) : (
                  <span>
                    {referenceDisplay(originTask) ?? 'an AI-proposed change'}
                  </span>
                )}
              </span>
            )}
            {children}
          </div>
        </div>
        {time && (
          <div
            data-testid="hover-card-history-time"
            className="w-30 shrink-0 text-right text-xs leading-4 text-muted-foreground"
          >
            <HoverDate
              date={time}
              format={timeFormat}
              tooltipDetail={timeTooltipDetail}
            />
          </div>
        )}
      </div>
      {nestedEvents && nestedEvents.length > 0 && (
        <div
          className="ml-5 flex flex-col gap-1"
          data-testid="activity-nested-events"
        >
          {nestedEvents.map((nestedEvent, idx) => (
            <div
              key={`${nestedEvent.time ?? 'nested'}-${idx}`}
              className="flex items-start gap-1 text-xs"
            >
              <IconRelatedArrow className="size-3.5 shrink-0" />
              <div className="min-w-0 flex-1 text-muted-foreground">
                {nestedEvent.author && (
                  <>
                    <HistoryAuthor author={nestedEvent.author} />{' '}
                  </>
                )}
                {nestedEvent.action}
                {nestedEvent.time && (
                  <>
                    {' '}
                    <HoverDate
                      date={nestedEvent.time}
                      format={nestedEvent.timeFormat ?? 'datetime'}
                    />
                  </>
                )}
                {nestedEvent.children}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function HistoryAvatar({ author }: { author?: Reference }): JSX.Element {
  if (author) {
    return (
      <Avatar profile={author} size="icon" className="mt-0.5 size-4 text-4xs" />
    )
  }
  return (
    <div className="mt-0.5 flex size-4 items-center justify-center rounded-full bg-muted">
      <Logo size={10} />
    </div>
  )
}

function HistoryAuthor({ author }: { author?: Reference }): JSX.Element {
  if (author && isOfType(author, 'Practitioner')) {
    return (
      <HoverPractitioner
        practitioner={author}
        roleInParens
        className="font-semibold text-foreground"
      />
    )
  }
  if (
    author &&
    (isOfType(author, 'ClientApplication') || isOfType(author, 'Bot'))
  ) {
    return (
      <span className="font-semibold text-foreground">
        {ExtendedClientApplication.companyName}
      </span>
    )
  }
  if (author && isOfType(author, 'Organization')) {
    return (
      <span className="font-semibold text-foreground">
        {referenceDisplay(author)}
      </span>
    )
  }
  if (author) {
    return <span className="font-semibold text-foreground">Unknown</span>
  }
  return <span className="font-semibold text-foreground">System</span>
}
